import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DsChipColorVariant, DsModule } from '@levelaccess/design-system';
import { VariationValue } from '@harnessio/ff-javascript-client-sdk';
import { combineLatest, concat, EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, exhaustMap, startWith, switchMap, take, takeWhile } from 'rxjs/operators';
import { TranslateService } from '../../translate/translate.service';

import { $scan, $scanScope } from '../../../../shared/constants/scan';
import { manualAuditStatus } from '../../../../shared/constants/scanning';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { IAiTestingPageResult, IManualScanEdit } from '../../../../shared/interfaces/scan.interface';
import { IPaginationModel } from '../../interfaces/pagination.interface';
import { ManualAuditService } from '../../services/manual-audit.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { UserService } from '../../services/user.service';
import { CommonPipesModule } from '../../shared/common-pipes.module';
import { DEFAULT_ENTRY_LIMITS } from '../../constants/form.constants';
import { PULLING_INTERVAL } from '../../shared/constants';
import { CommonUtility } from '../../utility/common.utility';
import {
  AiTestingPageDisplayStatus,
  buildAiTestingProgress,
  IAiTestingPageProgressRow,
  IAiTestingProgress,
  IAiTestingProgressPanelInputs,
} from './ai-testing-progress.utility';

interface IAiTestingProgressSnapshot {
  status: manualAuditStatus | undefined;
  progress: IAiTestingProgress;
}

@Component({
  selector: 'app-ai-testing-progress-panel',
  templateUrl: './ai-testing-progress-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DsModule, CommonPipesModule],
})
export class AiTestingProgressPanelComponent {
  public readonly workspaceId: InputSignal<string> = input.required<string>();
  public readonly digitalPropertyId: InputSignal<string> = input.required<string>();
  public readonly scanId: InputSignal<string> = input.required<string>();
  public readonly useAdminApi: InputSignal<boolean> = input.required<boolean>();

  protected readonly AiTestingPageDisplayStatus: typeof AiTestingPageDisplayStatus = AiTestingPageDisplayStatus;
  protected readonly pageSize: number = DEFAULT_ENTRY_LIMITS[1];

  private readonly manualAuditService: ManualAuditService = inject(ManualAuditService);
  private readonly userService: UserService = inject(UserService);
  private readonly featureFlagService: FeatureFlagService = inject(FeatureFlagService);
  private readonly translateService: TranslateService = inject(TranslateService);

  private readonly currentPage: WritableSignal<number> = signal(1);
  private readonly liveAnnouncement: WritableSignal<string> = signal('');
  private previousSnapshot: IAiTestingProgressSnapshot | null = null;
  // Alternates a trailing no-break space so repeated identical text still mutates the live region and is re-announced.
  private announcementToggle: boolean = false;

  private readonly target: Signal<IAiTestingProgressPanelInputs> = computed((): IAiTestingProgressPanelInputs => ({
    workspaceId: this.workspaceId(),
    digitalPropertyId: this.digitalPropertyId(),
    scanId: this.scanId(),
    useAdminApi: this.useAdminApi(),
  }));

  private readonly evaluation: Signal<IManualScanEdit | null> = toSignal(
    toObservable(this.target).pipe(
      switchMap((target: IAiTestingProgressPanelInputs): Observable<IManualScanEdit | null> =>
        this.watchEvaluation(target).pipe(startWith(null)),
      ),
    ),
    { initialValue: null },
  );

  private readonly snapshot: Signal<IAiTestingProgressSnapshot | null> = computed((): IAiTestingProgressSnapshot | null => {
    const evaluation: IManualScanEdit | null = this.evaluation();
    if (!evaluation) {
      return null;
    }
    const status: manualAuditStatus | undefined = evaluation[$scan.manualAuditStatus];
    const results: IAiTestingPageResult[] | undefined = evaluation[$scan.aiTestingPageResults];
    const hasResults: boolean = (results?.length ?? 0) > 0;
    const progress: IAiTestingProgress = buildAiTestingProgress(evaluation[$scan.scope]?.[$scanScope.pages], results, status);
    const isRelevant: boolean = status === manualAuditStatus.processing || hasResults;
    return isRelevant && progress.rows.length > 0 ? { status, progress } : null;
  });

  protected readonly isVisible: Signal<boolean> = computed((): boolean => this.snapshot() !== null);

  protected readonly header: Signal<string> = computed((): string => {
    const snapshot: IAiTestingProgressSnapshot | null = this.snapshot();
    return snapshot ? this.buildHeader(snapshot.progress) : '';
  });

  protected readonly pagination: Signal<IPaginationModel> = computed((): IPaginationModel => {
    const total: number = this.snapshot()?.progress.rows.length ?? 0;
    return CommonUtility.calculateColumnsPagination(total, this.currentPage(), this.pageSize);
  });

  protected readonly pageRows: Signal<IAiTestingPageProgressRow[]> = computed((): IAiTestingPageProgressRow[] => {
    const rows: IAiTestingPageProgressRow[] = this.snapshot()?.progress.rows ?? [];
    const pagination: IPaginationModel = this.pagination();
    return rows.slice(pagination.startIndex, pagination.endIndex + 1);
  });

  protected readonly totalRows: Signal<number> = computed((): number => this.snapshot()?.progress.rows.length ?? 0);
  protected readonly announcement: Signal<string> = this.liveAnnouncement.asReadonly();

  constructor() {
    effect((): void => {
      const snapshot: IAiTestingProgressSnapshot | null = this.snapshot();
      untracked((): void => this.updateAnnouncement(snapshot));
    });
  }

  protected onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  protected getStatusText(status: AiTestingPageDisplayStatus): string {
    return this.translateService.instant(`ai_testing_progress_status_${status}`);
  }

  protected getChipVariant(status: AiTestingPageDisplayStatus): DsChipColorVariant {
    switch (status) {
      case AiTestingPageDisplayStatus.completed:
        return DsChipColorVariant.Success;
      case AiTestingPageDisplayStatus.failed:
        return DsChipColorVariant.Danger;
      default:
        return DsChipColorVariant.Info;
    }
  }

  /**
   * Fetches the evaluation and keeps polling while it is processing. Every request is caught, never rethrown:
   * an unhandled 403 would make ErrorHandlerService redirect the whole page to /forbidden.
   * A failed initial fetch emits nothing and stops. A failed poll is skipped and retried on the next tick.
   */
  private watchEvaluation(target: IAiTestingProgressPanelInputs): Observable<IManualScanEdit> {
    return combineLatest([
      this.userService.hasTenantStaffAccess(),
      this.featureFlagService.variation$(FeatureFlagCollection.asyncAiTesting, false),
    ]).pipe(
      take(1),
      catchError((): Observable<never> => EMPTY),
      switchMap(([isStaff, isEnabled]: [boolean, VariationValue]): Observable<IManualScanEdit> => {
        if (!isStaff || !isEnabled) {
          return EMPTY;
        }
        return this.fetchEvaluation(target).pipe(
          catchError((): Observable<never> => EMPTY),
          switchMap((initial: IManualScanEdit): Observable<IManualScanEdit> => {
            if (!this.isProcessing(initial)) {
              return of(initial);
            }
            return concat(
              of(initial),
              timer(PULLING_INTERVAL.SLOW, PULLING_INTERVAL.SLOW).pipe(
                exhaustMap((): Observable<IManualScanEdit> =>
                  this.fetchEvaluation(target).pipe(catchError((): Observable<never> => EMPTY)),
                ),
                takeWhile((evaluation: IManualScanEdit): boolean => this.isProcessing(evaluation), true),
              ),
            );
          }),
        );
      }),
    );
  }

  private fetchEvaluation(target: IAiTestingProgressPanelInputs): Observable<IManualScanEdit> {
    return this.manualAuditService.getEvaluation(target.workspaceId, target.digitalPropertyId, target.scanId, {
      useAdminApi: target.useAdminApi,
      skipLoader: true,
    });
  }

  private isProcessing(evaluation: IManualScanEdit): boolean {
    return evaluation?.[$scan.manualAuditStatus] === manualAuditStatus.processing;
  }

  private buildHeader(progress: IAiTestingProgress): string {
    const { completed, processing, failed } = progress.summary;
    const segments: string[] = [];
    if (completed > 0) {
      const key: string =
        completed === 1 ? 'ai_testing_progress_pages_complete_singular' : 'ai_testing_progress_pages_complete_plural';
      segments.push(this.translateService.instant(key, [completed]));
    }
    if (processing > 0) {
      segments.push(this.translateService.instant('ai_testing_progress_processing', [processing]));
    }
    if (failed > 0) {
      segments.push(this.translateService.instant('ai_testing_progress_failed', [failed]));
    }
    return segments.join(', ');
  }

  /** Announces only when the failed count increases or the status leaves processing; never on the first render. */
  private updateAnnouncement(snapshot: IAiTestingProgressSnapshot | null): void {
    const previous: IAiTestingProgressSnapshot | null = this.previousSnapshot;
    this.previousSnapshot = snapshot;
    if (!snapshot || !previous) {
      return;
    }
    const failedIncreased: boolean = snapshot.progress.summary.failed > previous.progress.summary.failed;
    const leftProcessing: boolean =
      previous.status === manualAuditStatus.processing && snapshot.status !== manualAuditStatus.processing;
    if (failedIncreased || leftProcessing) {
      this.announcementToggle = !this.announcementToggle;
      this.liveAnnouncement.set(`${this.buildHeader(snapshot.progress)}${this.announcementToggle ? '\u00A0' : ''}`);
    }
  }
}
